import {
  Component,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  inject,
  ElementRef,
  HostListener,
  ChangeDetectorRef
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  TableComponent,
  TableModule,
  ModalModule,
  TableConfig,
  PaginatorFetcher,
  PaginatorFilters,
  EmptyStateModule
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map, switchMap, timer } from 'rxjs';
import { TranslationService } from '../../translations/services/translation.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

// --- Local model definitions (moved from user-management) ---
interface Customer {
  customerPk: string;
  customerName: string;
  code: string;
  customerType: string;
  city: string;
  stateProvince: string;
  turnAroundTime?: number;
  fiscalIdNum?: string;
  customerNotes?: string;
  preferredTreadSpecificationPk?: string;
  rtDot?: string;
  treadDepth?: string;
  disposalChargesFk?: string;
  plantFks?: string; // Comma-separated string of plant IDs, or "ALL" if associated with all plants
  rememberBrand?: string;
  rememberCasingNote?: string;
}
interface PaginatedCustomer {
  customerSearchDTOs: Customer[];
  pagination: { totalRecords: number };
}
interface PaginatedCustomerResponse {
  data: PaginatedCustomer;
  dataList: PaginatedCustomer[];
  error: Array<string>;
}

@Component({
  selector: 'mic-customer-select-model',
  templateUrl: './customer-select-model.component.html',
  styleUrls: ['./customer-select-model.component.scss'],
  standalone: true,
  imports: [ModalModule, TableModule, EmptyStateModule]
})
export class CustomerSelectModelComponent implements OnInit {
  @ViewChild(TableComponent, { static: false }) tableRef: TableComponent<Customer>;
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @Output() result = new EventEmitter<Customer>();

  @Input() dealerId: string;
  @Input() userLogin: string;
  @Input() plantId: string;
  // Optional filters for Finding 7: Active & Type Filtering (backward compatible)
  @Input() activeOnly?: boolean; // If true, only return active customers
  @Input() customerTypes?: string[]; // Filter by customer types (e.g., ['CUSTOMER', 'ASSOCIATE_DEALER'])
  @Input() showCnpj?: boolean = false; // If true, show CNPJ column and enable CNPJ search (backward compatible - defaults to false)
  @Input() cnpjColumnHeader?: string = 'CNPJ'; // Column header text for CNPJ column (defaults to 'CNPJ')
  public translationService = inject(TranslationService);
  public store = inject(Store);
  public http = inject(HttpClient);
  public elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  searchControl: FormControl = new FormControl<string>('');
  customers: Customer[] = [];
  pageSizeOptions = [5, 10, 25, 100];
  pageSize = 10;
  currentPage = 1;
  tableConfig: TableConfig<Customer>;
  filtersConfig: any;

  // Stable fetcher reference — defined at class level so mic-table receives it only once
  // (on the same CD cycle as tableConfig), preventing a double-fetch on initialization.
  // All input values (activeOnly, customerTypes, showCnpj, etc.) are read at call-time.
  fetcher: PaginatorFetcher<Customer> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    let sortField = '';
    let sortDirection = '';
    if (filters.sort) {
      const [field, direction] = filters.sort.split(':');
      sortField = field;
      sortDirection = direction;
    }

    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchUserCustomer}`;
    const headers = new HttpHeaders({
      dealerId: this.dealerId || '',
      userLogin: this.userLogin || '',
      plantId: this.plantId || ''
    });
    let params = new HttpParams()
      .set('searchString', this.searchControl.value ?? '')
      .set('page', filters.page)
      .set('pageSize', filters.limit)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('searchCnpj', String(this.showCnpj || false));

    if (this.activeOnly !== undefined && this.activeOnly !== null) {
      params = params.set('activeOnly', String(this.activeOnly));
    }
    if (this.customerTypes && this.customerTypes.length > 0) {
      params = params.set('customerTypes', this.customerTypes.join(','));
    }

    return timer(0).pipe(
      switchMap(() => this.http.get<PaginatedCustomerResponse>(url, { headers, params })),
      map((response: PaginatedCustomerResponse) => {
        const customers = response?.data?.customerSearchDTOs ?? [];
        const count = response?.data?.pagination?.totalRecords ?? 0;
        const hasError = Array.isArray(response?.error) && response.error.length > 0;

        if (hasError) {
          return { items: [], count: 0 };
        }

        this.customers = customers;
        return { items: customers, count };
      })
    );
  };

  /** Index of the visually highlighted row; -1 means no row is highlighted. */
  highlightedRowIndex = -1;

  // NOTE: Table configuration is initialized in ngOnInit after showCnpj input is available

  /** Called by the parent after the dialog opens to focus the search input. */
  focusSearchInput(): void {
    this.searchInputRef?.nativeElement?.focus();
  }

  /**
   * Keyboard navigation within the dialog.
   * Focus always stays on the search input so the user can type and navigate simultaneously.
   *
   * ArrowDown  — move highlight to the next row (or first row if nothing is highlighted)
   * ArrowUp    — move highlight to the previous row; if on the first row, clear highlight
   * Enter      — select the highlighted row; if nothing highlighted, select the first result
   * Escape     — clear highlight and return focus to the search input
   */
  @HostListener('keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    const rows = this.getTableRows();
    if (!rows.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.applyHighlight(this.highlightedRowIndex < 0 ? 0 : Math.min(this.highlightedRowIndex + 1, rows.length - 1), rows);
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.highlightedRowIndex <= 0) {
          this.applyHighlight(-1, rows);
          this.searchInputRef?.nativeElement?.focus();
        } else {
          this.applyHighlight(this.highlightedRowIndex - 1, rows);
        }
        break;

      case 'Enter': {
        const targetIndex = this.highlightedRowIndex >= 0 ? this.highlightedRowIndex : 0;
        if (this.customers[targetIndex]) {
          event.preventDefault();
          this.result.emit(this.customers[targetIndex]);
        }
        break;
      }

      case 'Escape':
        event.preventDefault();
        this.applyHighlight(-1, rows);
        this.searchInputRef?.nativeElement?.focus();
        break;
    }
  }

  /** Returns all data rows from the embedded mic-table. */
  private getTableRows(): HTMLElement[] {
    return Array.from((this.elementRef.nativeElement as HTMLElement).querySelectorAll('tbody tr')) as HTMLElement[];
  }

  /**
   * Applies `keyboard-selected` CSS class to the row at `index`.
   * Removes the class from all other rows.
   * Scrolls the newly highlighted row into view.
   */
  private applyHighlight(index: number, rows: HTMLElement[]): void {
    rows.forEach((r) => r.classList.remove('keyboard-selected'));
    this.highlightedRowIndex = index;
    if (index >= 0 && rows[index]) {
      rows[index].classList.add('keyboard-selected');
      rows[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  private initializeTableConfig(): void {
    // Build columns object dynamically based on showCnpj flag
    const columns: any = {
      code: {
        cell: (elm: Customer) => elm.code,
        header: 'Customer Code',
        sortable: true
      },
      customerName: {
        cell: (elm: Customer) => elm.customerName,
        header: 'Customer Name',
        sortable: true
      }
    };

    // Conditionally add CNPJ column if showCnpj is true
    if (this.showCnpj) {
      columns['fiscalIdNum'] = {
        cell: (elm: Customer) => elm.fiscalIdNum || '',
        header: this.cnpjColumnHeader || 'CNPJ',
        sortable: true
      };
    }

    // Add remaining columns
    columns['city'] = {
      cell: (elm: Customer) => elm.city,
      header: 'City',
      sortable: true
    };
    columns['stateProvince'] = {
      cell: (elm: Customer) => elm.stateProvince,
      header: 'State',
      sortable: true
    };

    this.tableConfig = {
      paginator: {
        pageSize: this.pageSize,
        pageSizeOptions: this.pageSizeOptions,
        forceBottomAction: true,
        position: 'bottom'
      },
      sort: {
        default: 'customerName:asc'
      },
      columns: columns,
      onRowClick: (item: Customer) => {
        this.result.emit(item);
      },
      // Sync mouse hover position into keyboard nav state so mouse→keyboard handoff is seamless:
      // hovering row N then pressing ArrowDown moves to row N+1 instead of restarting from top.
      // Also clears keyboard-selected styling since Michelin's built-in row hover CSS takes over.
      onRowHover: (elm: Customer) => {
        this.highlightedRowIndex = this.customers.indexOf(elm);
        const rows = this.getTableRows();
        rows.forEach((r) => r.classList.remove('keyboard-selected'));
      }
    };

    // Build filtersConfig dynamically based on showCnpj flag
    const filterColumns: any = {
      code: { type: 'text' },
      customerName: { type: 'text' }
    };

    // Conditionally add CNPJ filter if showCnpj is true
    if (this.showCnpj) {
      filterColumns['fiscalIdNum'] = { type: 'text' };
    }

    // Add remaining filter columns
    filterColumns['city'] = { type: 'text' };
    filterColumns['stateProvince'] = { type: 'text' };

    this.filtersConfig = {
      columns: filterColumns
    };
  }

  ngOnInit() {
    // Initialize table configuration now that input properties (including showCnpj) are available
    this.initializeTableConfig();

    this.searchControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      // Reset row highlight whenever the search query changes (results will reload)
      this.highlightedRowIndex = -1;
      if (this.tableRef) {
        this.tableRef.patchExtraFilters({ searchString: this.searchControl.value });
      }
    });
  }
}
